import React from "react";
import { motion } from "framer-motion";
import { Target, Flame, Dumbbell, Users } from "lucide-react";

const ABOUT_IMG = "https://images.unsplash.com/photo-1628935291759-bbaf33a66dc6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxmaXRuZXNzJTIwdHJhaW5lciUyMGRhcmt8ZW58MHx8fHwxNzc0Nzg2NDA5fDA&ixlib=rb-4.1.0&q=85";

const stats = [
  { icon: Dumbbell, value: "50+", label: "Equipment Stations" },
  { icon: Users, value: "2K+", label: "Active Members" },
  { icon: Flame, value: "19hrs", label: "Daily Open" },
  { icon: Target, value: "4", label: "Signature Programs" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.15, ease: "easeOut" },
  }),
};

export default function AboutSection() {
  return (
    <section id="about" data-testid="about-section" className="py-20 sm:py-24 lg:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Section label */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeUp}
          custom={0}
          className="mb-4"
        >
          <span className="text-[#F59E0B] text-sm font-semibold tracking-[0.3em] uppercase">About Us</span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text */}
          <div className="space-y-8">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeUp}
              custom={1}
              className="font-['Bebas_Neue'] text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white leading-[0.95]"
            >
              Where Discipline
              <br />
              <span className="text-[#F59E0B]">Becomes Identity</span>
            </motion.h2>

            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeUp}
              custom={2}
              className="text-[#A3A3A3] text-base leading-relaxed max-w-lg"
            >
              Core Culture is a next-generation fitness space built around one idea: training isn't just physical — it's a lifestyle. Designed for a new wave of athletes and creators, we blend high-performance workouts with a bold, aesthetic-driven environment.
            </motion.p>

            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeUp}
              custom={3}
              className="text-[#A3A3A3] text-base leading-relaxed max-w-lg"
            >
              From heavy lifts to late-night sessions, every corner is built to push limits — and look good doing it. With matte black interiors, LED strips, and mirror-heavy layouts, we're not just about getting stronger. We're about building confidence and a mindset that carries beyond the gym.
            </motion.p>

            {/* Stats row */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-6 border-t border-[#262626]"
            >
              {stats.map((s, i) => (
                <motion.div key={s.label} variants={fadeUp} custom={i + 4} className="text-center lg:text-left">
                  <s.icon className="w-5 h-5 text-[#F59E0B] mb-2 mx-auto lg:mx-0" />
                  <div className="font-['Bebas_Neue'] text-3xl text-white tracking-wide">{s.value}</div>
                  <div className="text-xs text-[#A3A3A3] tracking-wider uppercase mt-1">{s.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative overflow-hidden aspect-[4/5]">
              <img
                src={ABOUT_IMG}
                alt="Core Culture Gym"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
            </div>
            {/* Accent line */}
            <div className="absolute -bottom-4 -left-4 w-24 h-24 border-l-2 border-b-2 border-[#F59E0B]" />
            <div className="absolute -top-4 -right-4 w-24 h-24 border-t-2 border-r-2 border-[#F59E0B]" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
